import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const highlights = [
  ['Location', 'Chennai, India'],
  ['Focus', 'Websites and UI'],
  ['Degree', 'BCA'],
  ['Languages', 'English, Tamil'],
];

const strengths = [
  'Responsive layouts',
  'Modern landing pages',
  'Dashboard UI',
  'Portfolio websites',
  'Business websites',
  'Performance polish',
];

export default function About() {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section id="about" ref={ref} className="section-pad alt">
      <div className="ghost-text">ABOUT</div>

      <div className="section-shell">
        <div className="about-grid" style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.1fr', gap: 64, alignItems: 'center' }}>
          <motion.div variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} style={{ position: 'relative' }}>
            <div className="premium-card shine" style={{ padding: 10, overflow: 'hidden' }}>
              <img
                src="/img/about.jpg"
                alt="Prakash C working on React, Flutter, and web development projects in Chennai"
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: 560, objectFit: 'cover', display: 'block', borderRadius: 6 }}
                onError={event => { event.currentTarget.src = '/img/profile.jpg'; }}
              />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.45 }}
              className="premium-card about-badge"
              style={{
                position: 'absolute',
                right: -22,
                bottom: 28,
                width: 220,
                padding: 22,
                background: 'linear-gradient(135deg, rgba(54,234,211,0.18), rgba(255,77,141,0.14)), var(--panel)',
              }}
            >
              <div className="color-line" style={{ marginBottom: 18 }} />
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 34, lineHeight: 1 }}>2+</div>
              <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
                Years of building polished web experiences.
              </p>
            </motion.div>
          </motion.div>

          <div>
            <motion.div variants={fadeUp} custom={0} initial="hidden" animate={inView ? 'visible' : 'hidden'}>
              <div className="eyebrow">Get to know me</div>
              <h2 className="section-title">Designer eye, developer hands, colorful delivery.</h2>
              <p className="section-copy">
                I turn ideas into clean, attractive websites that feel premium on desktop and mobile. My work balances strong visuals, simple navigation, fast interactions, and practical business goals.
              </p>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              className="about-highlights"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, margin: '32px 0' }}
            >
              {highlights.map(([label, value], index) => (
                <div
                  key={label}
                  className="premium-card"
                  style={{
                    padding: 18,
                    boxShadow: 'none',
                    borderTop: `3px solid ${index % 4 === 0 ? 'var(--aqua)' : index % 4 === 1 ? 'var(--rose)' : index % 4 === 2 ? 'var(--sun)' : 'var(--lime)'}`,
                  }}
                >
                  <div style={{ color: 'var(--muted)', fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                    {label}
                  </div>
                  <div style={{ color: 'var(--white)', fontFamily: 'var(--font-display)', fontWeight: 800, marginTop: 5 }}>{value}</div>
                </div>
              ))}
            </motion.div>

            <motion.div variants={fadeUp} custom={2} initial="hidden" animate={inView ? 'visible' : 'hidden'} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 34 }}>
              {strengths.map(skill => <span key={skill} className="chip">{skill}</span>)}
            </motion.div>

            <motion.a variants={fadeUp} custom={3} initial="hidden" animate={inView ? 'visible' : 'hidden'} href="mailto:prakash12991299@gmail.com" className="btn-primary">
              Hire Me
            </motion.a>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 920px) {
          .about-grid { grid-template-columns: 1fr !important; gap: 44px !important; }
          .about-grid img { height: 440px !important; }
        }
        @media (max-width: 620px) {
          .about-highlights { grid-template-columns: 1fr !important; }
          .about-badge { position: static !important; width: auto !important; margin-top: 14px; }
        }
      `}</style>
    </section>
  );
}
